/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {Component, ElementRef, Input, ViewChild} from '@angular/core';
import {QRCodeComponent} from 'angularx-qrcode';

const MIN_FRAME_WIDTH = 3;
const FRAME_WIDTH_RATIO = 0.02;
const MIN_TRIANGLE_SIZE = 20;
const TRIANGLE_SIZE_RATIO = 0.16;
const LABEL_HEIGHT_OFFSET = 18;
const FRAME_SIDES = 2;
const LABEL_TEXT_Y_PADDING = 2;
const LABEL_FONT_SIZE = 14;

/**
 * Renders a QR code with an IEC 61406-1:2022 compliant frame
 *
 * IEC 61406-1:2022 / DIN SPEC 91406 compliance:
 *  - Frame: black rectangular border (1X line width) around the full symbol
 *  - Corner indicator: black right-angle triangle (3X × 3X) in the
 *    bottom-right corner
 *  - Fixed dark/light colours (#000000 / #ffffff) regardless of UI theme
 */
@Component({
  selector: 'app-iec-qr-code',
  templateUrl: './iec-qr-code.component.html',
  styleUrl: './iec-qr-code.component.scss',
  standalone: false,
})
export class IecQrCodeComponent {
  @Input() qrdata = '';
  @Input() size = 240;
  @Input() label?: string;

  @ViewChild('outputCanvas', {static: true})
  outputCanvas!: ElementRef<HTMLCanvasElement>;

  @ViewChild(QRCodeComponent, {static: false})
  private readonly qrcodeComp!: QRCodeComponent;

  private get frameWidth(): number {
    return Math.max(MIN_FRAME_WIDTH, Math.round(this.size * FRAME_WIDTH_RATIO));
  }

  private get triangleSize(): number {
    return Math.max(
      MIN_TRIANGLE_SIZE,
      Math.round(this.size * TRIANGLE_SIZE_RATIO),
    );
  }

  onQrReady(): void {
    setTimeout(() => {
      const srcCanvas =
        this.qrcodeComp?.qrcElement?.nativeElement?.querySelector('canvas');
      if (!(srcCanvas instanceof HTMLCanvasElement)) {
        return;
      }
      this.drawComposite(srcCanvas);
    }, 0);
  }

  private drawComposite(srcCanvas: HTMLCanvasElement): void {
    const fw = this.frameWidth;
    const qrW = srcCanvas.width;
    const qrH = srcCanvas.height;
    const labelH = this.label ? fw + LABEL_HEIGHT_OFFSET : 0;
    const totalW = qrW + FRAME_SIDES * fw;
    const totalH = qrH + FRAME_SIDES * fw;

    const canvas = this.outputCanvas.nativeElement;
    canvas.width = totalW;
    canvas.height = totalH + labelH;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.drawImage(srcCanvas, fw, fw);

    ctx.strokeStyle = '#000000';
    ctx.lineWidth = fw;
    ctx.strokeRect(fw / FRAME_SIDES, fw / FRAME_SIDES, qrW + fw, qrH + fw);

    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.moveTo(totalW, totalH);
    ctx.lineTo(totalW - this.triangleSize, totalH);
    ctx.lineTo(totalW, totalH - this.triangleSize);
    ctx.closePath();
    ctx.fill();

    if (this.label) {
      ctx.fillStyle = '#000000';
      ctx.font = `bold ${LABEL_FONT_SIZE}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(
        this.label,
        totalW / FRAME_SIDES,
        totalH + fw + LABEL_TEXT_Y_PADDING,
      );
    }
  }
}
